import { useAuth } from "@workos-inc/authkit-react";
import { useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router";
import Sidebar from "./components/sidebar";
import { useAdminMode } from "./contexts/AdminModeContext";
import "./App.css";
import { ColorProvider } from "./contexts/ColorContext";
import { appConfig } from "@/appConfig";

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, isLoading, signIn } = useAuth();
  const { adminMode } = useAdminMode();

  useEffect(() => {
    if (location.pathname.includes("login")) {
      const searchParams = new URLSearchParams(location.search);
      const context = searchParams.get("context") ?? undefined;
      signIn({ context });
    }
  }, [location.pathname, location.search, signIn]);

  useEffect(() => {
    if (!isLoading && user) {
      const userRole = role;
      const currentPath = location.pathname;

      // Allow home page for all authenticated users
      if (currentPath === "/home" || currentPath === "/") {
        return;
      }

      if (userRole === "member" && !currentPath.startsWith("/assistant")) {
        navigate("/assistant", { replace: true });
      }

      if (userRole === "admin" || userRole === "superadmin") {
        // If admin/superadmin is in member mode, redirect to assistant
        if (adminMode === "member" && !currentPath.startsWith("/assistant")) {
          navigate("/assistant", { replace: true });
        }
        // If admin/superadmin is in admin mode, redirect to admin pages
        else if (adminMode === "admin") {
          const allowedAdminRoutes = [
            "/documents",
            "/users",
            "/answers",
            "/settings",
            "/analytics",
          ];
          const pathSegments = currentPath.split("/").filter(Boolean);
          const isAllowedAdminRoute = allowedAdminRoutes.some((route) => {
            const normalizedRoute = route.replace(/^\//, "");
            if (
              currentPath === route ||
              currentPath.startsWith(`${route}/`)
            ) {
              return true;
            }
            return pathSegments.includes(normalizedRoute);
          });

          if (!isAllowedAdminRoute) {
            navigate("/documents", { replace: true });
          }
        }
      }
    }
  }, [user, role, isLoading, navigate, adminMode, location.pathname]);

  if (isLoading) {
    // Use same color logic as Home page - read from localStorage for demo mode
    const loadingColor = appConfig.features.isDemoMode
      ? (localStorage.getItem("themeColor") || "#2563EB")
      : appConfig.themeColor;
    
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="flex space-x-2 transform translate-y-[0.75px]">
          <div className="w-2 h-2 rounded-full animate-bounce [animation-delay:-0.3s]" style={{ backgroundColor: loadingColor }} />
          <div className="w-2 h-2 rounded-full animate-bounce [animation-delay:-0.15s]" style={{ backgroundColor: loadingColor }} />
          <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: loadingColor }} />
        </div>
      </div>
    );
  }

  if (!user) {
    // Preserve current URL on hard refresh so after auth we can return here.
    // If user explicitly goes to /auth, we won't set returnTo and they'll land on /home.
    const returnTo = `${location.pathname}${location.search || ""}`;
    if (
      returnTo &&
      returnTo.startsWith("/") &&
      returnTo !== "/" &&
      returnTo !== "/home" &&
      !returnTo.startsWith("/auth")
    ) {
      sessionStorage.setItem("returnTo", returnTo);
    }
    navigate("/auth");
    return null;
  }

  // Hide sidebar on home page
  const isHomePage = location.pathname === "/home" || location.pathname === "/";

  return (
    <ColorProvider>
      <div
        className="flex h-screen w-screen"
        style={{
          background: `linear-gradient(0deg, #FFFFFF, #FFFFFF), linear-gradient(180deg, rgba(238, 242, 255, 0) 81.26%, #EEF2FF 99.1%)`,
        }}
      >
        {!isHomePage && <Sidebar />}
        <main className={`flex-1 overflow-hidden ${isHomePage ? "" : "pt-16 lg:pt-0"}`}>
          <Outlet />
        </main>
      </div>
    </ColorProvider>
  );
};

export default App;
